import {authQueryOptions, useAuth} from '@/modules/auth/api'
import {initializeUserSession} from '@/modules/auth/functions'
import {getLicenceStateOptions, useLicenceCountry} from '@/modules/licenses/api'
import {useShortcuts} from '@/modules/shortcuts/hooks/use-shortcuts'
import {useReportExportNotifications} from '@/modules/reports/hooks/use-report-export-notifications'
import {useSuspenseGetUser} from '@/modules/users/api'
import {rqContext} from '@/router'
import {useCan} from '@/shared/hooks/use-permissions'
import {swipeableDrawer} from '@/shared/components/ui/swipeable-drawer'
import {getSearchStateForPathname, persistentSearchStateRoutes, saveSearchState} from '@/shared/utils/search-state'
import {Box, Center} from '@chakra-ui/react'
import {dialog, drawer, floatingPanel} from '@cypro/ui/components'
import {LoadingLogo} from '@cypro/ui/components/ui/loading-logo'
import {Logo} from '@cypro/ui/components/ui/logo'
import {Toaster} from '@cypro/ui/components/ui/toaster'
import {DefaultCountryProvider} from '@cypro/ui/hooks/default-country'
import {toaster} from '@cypro/ui/toaster'
import {RouterContext} from '@cypro/ui/types'
import {titleCase} from '@cypro/ui/utils'
import {useConnectionState} from '@rocicorp/zero/react'
import {TanStackDevtools} from '@tanstack/react-devtools'
import {ReactQueryDevtoolsPanel} from '@tanstack/react-query-devtools'
import {
  createRootRouteWithContext,
  HeadContent,
  LinkProps,
  Outlet,
  Scripts,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import {TanStackRouterDevtoolsPanel} from '@tanstack/react-router-devtools'
import {Suspense, useEffect} from 'react'
import type {Country} from 'react-phone-number-input'
import * as z from 'zod'
import css from '../styles.css?url'

export const Route = createRootRouteWithContext<RouterContext>()({
  loader: async () => {
    const auth = await rqContext.queryClient.ensureQueryData(authQueryOptions)
    await rqContext.queryClient.ensureQueryData(getLicenceStateOptions)
    initializeUserSession({auth})
  },
  head: ({matches}) => {
    const titleParts = ['Cypro']
    const lastMatch = matches.at(-1)

    if (lastMatch) {
      const lastPatchPathParts = lastMatch.pathname.split('/')
      const formattedParts = lastPatchPathParts
        .filter(part => {
          const isDashboard = part.toLowerCase().includes('dashboard')
          if (isDashboard) return false
          const {success: isUuid} = z.uuid().safeParse(part)
          if (isUuid) return false
          return true
        })
        .map(part => part.split('-').map(titleCase).join(' '))
        .filter(Boolean)
      titleParts.push(...formattedParts)
    }

    return {
      meta: [
        {charSet: 'utf-8'},
        {name: 'viewport', content: 'width=device-width, initial-scale=1'},
        {title: titleParts.toReversed().join(' - ')},
      ],
      links: [
        {rel: 'icon', href: '/favicon.png'},
        {rel: 'stylesheet', href: css},
      ],
    }
  },
  shellComponent: RootDocument,
  errorComponent: ErrorComponent,
})

function RootDocument() {
  const router = useRouter()
  const zeroConnection = useConnectionState()
  const navigate = useNavigate()
  const {auth} = useAuth()
  const licenceCountry = useLicenceCountry()
  // the export notification poll hits an endpoint that needs reports.view, so do not start it without it
  const canViewReports = useCan('reports.view')

  // Right after login, Zero re-creates its client against the newly authenticated
  // context and has to complete its connect handshake before server-side queries and
  // mutators see an authenticated ctx.user. Rendering the outlet before that finishes
  // is what causes the "not authenticated" error that only a hard refresh fixes.
  const isZeroReadyForAuthedUser = !auth?.user || zeroConnection.name !== 'connecting'

  useEffect(() => {
    const onBeforeRouteMountUnsubscribe = router.subscribe('onBeforeRouteMount', args => {
      drawer.removeAll()
      dialog.removeAll()

      const {fromLocation, toLocation} = args

      if (fromLocation && persistentSearchStateRoutes.has(fromLocation.pathname as LinkProps['to'])) {
        // save the search state after the user leaves a page
        saveSearchState(fromLocation.pathname, fromLocation.searchStr)
      }

      // if navigating to a diferent page, attempt to load the last saved search state
      if (
        fromLocation?.pathname !== toLocation?.pathname &&
        persistentSearchStateRoutes.has(toLocation.pathname as LinkProps['to'])
      ) {
        const savedFilter = getSearchStateForPathname(toLocation.pathname)
        if (savedFilter && toLocation.searchStr !== savedFilter) {
          router.navigate({to: `${toLocation.pathname}${savedFilter}`, replace: true, ignoreBlocker: true})
        }
      }
    })

    function onUncaughtPromise(event: PromiseRejectionEvent) {
      const reason = event.reason
      let message = 'Unknown error'

      if (
        (reason instanceof Error && reason.message) ||
        (typeof reason === 'object' && 'message' in reason && typeof reason.message === 'string')
      ) {
        message = reason.message
      }

      toaster.error({title: message})
    }

    window.addEventListener('unhandledrejection', onUncaughtPromise)

    return () => {
      onBeforeRouteMountUnsubscribe()
      window.removeEventListener('unhandledrejection', onUncaughtPromise)
    }
  }, [router])

  useEffect(() => {
    async function checkConnection() {
      console.log({zeroConnection})

      switch (zeroConnection.name) {
        case 'needs-auth':
          if (!router.state.location.pathname.startsWith('/auth')) {
            await rqContext.queryClient.invalidateQueries()
            await navigate({to: '/auth/login', search: {location: router.state.location.pathname}})
          }
          break
      }
    }

    checkConnection()
  }, [zeroConnection, navigate, router])

  useShortcuts()
  // finished background exports are announced wherever the user is, not only in the export dialog
  useReportExportNotifications(Boolean(auth?.user) && canViewReports)

  return (
    <DefaultCountryProvider country={licenceCountry as Country | undefined}>
      <HeadContent />

      <Toaster />
      <dialog.Viewport />
      <drawer.Viewport />
      <floatingPanel.Viewport />
      <swipeableDrawer.Viewport />

      {isZeroReadyForAuthedUser ? (
        <Suspense fallback={<LoadingLogo height="100dvh" />}>
          <AuthedUserPrefetch />
        </Suspense>
      ) : (
        <LoadingLogo height="100dvh" />
      )}

      <Scripts />

      {import.meta.env.DEV && (
        <TanStackDevtools
          config={{position: 'bottom-right'}}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
            {name: 'Tanstack Query', render: <ReactQueryDevtoolsPanel />},
          ]}
        />
      )}
    </DefaultCountryProvider>
  )
}

function ErrorComponent() {
  return (
    <Center height="100dvh">
      <Box asChild animation="ping">
        <Logo size={64} hideText />
      </Box>
    </Center>
  )
}

/**
 * Fetches the current user (including their groups) before rendering the app
 * tree, suspending until it resolves.
 *
 * The therapy-sequence state machine hydrates its actor from the user's groups
 * only when the actor is created (`useActor` rehydrates solely on mount), and
 * the groups load asynchronously. If a drawer were opened before they arrived,
 * the actor would be created with empty groups and reject actions the user is
 * entitled to. Pre-fetching the user here keeps the query warm in Zero's cache,
 * so every downstream `useGetUser` resolves synchronously with the groups already
 * populated and the actor never needs to be recreated.
 */
function AuthedUserPrefetch() {
  const {auth} = useAuth()
  useSuspenseGetUser(auth?.user?.id)
  return <Outlet />
}
